/**
 * Converts a given size in bytes into a human-readable format.
 *
 * @param {number} bytes - The size in bytes to be converted. Must be a non-negative integer.
 * @returns {string} A string representing the size in a human-readable format (e.g., "1 KB", "2.5 MB").
 *
 * The function works by determining the appropriate unit (Bytes, KB, MB, etc.) based on the input size
 * and formatting the value to one decimal place if necessary. It uses a logarithmic calculation to find
 * the index of the unit and divides the input size by the corresponding power of 1024.
 *
 * Dependencies:
 * - None
 *
 * Example usage:
 * ```typescript
 * console.log(convertBytesToReadableSize(0)); // Output: "0 Bytes"
 * console.log(convertBytesToReadableSize(1024)); // Output: "1 KB"
 * console.log(convertBytesToReadableSize(1048576)); // Output: "1 MB"
 * console.log(convertBytesToReadableSize(123456789)); // Output: "117.7 MB"
 * ```
 */
export const convertBytesToReadableSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const K_UNIT = 1024;
  const SIZE_UNITS = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

  const unitIndex = Math.floor(Math.log(bytes) / Math.log(K_UNIT));
  const valueInUnit = bytes / K_UNIT ** unitIndex;

  const formattedValue = valueInUnit % 1 === 0 ? valueInUnit : valueInUnit.toFixed(1);

  return `${formattedValue} ${SIZE_UNITS[unitIndex]}`;
};
